/**
 * Money on the client: minor units in, text out, and text in, minor units back.
 *
 * Three rules from `model.md` §2.2 survive the trip to the browser:
 *
 *  1. Amounts are signed integers in **minor units**. Expenses are negative.
 *  2. The scale is the currency's, never a division by 100 — JPY has no minor
 *     unit, most currencies have two, a few have three.
 *  3. Nothing passes through a float on the way in. `parseAmount` splits the
 *     text at the decimal separator and does integer arithmetic on each half,
 *     so "0,07" cannot arrive as 0.06999999999999999.
 *
 * Rule 2 needs the digit count per currency. It comes from the browser's own
 * CLDR data rather than a table maintained here, and `money.test.ts` pins what
 * the browser reports against the values the model states for every seeded
 * currency — so a disagreement fails the build instead of quietly mis-rendering
 * somebody's balance.
 */

import type { MessageKey } from "@/i18n/catalog";
import type { Locale } from "@/i18n/locales";
import type { Params } from "@/i18n/translate";

const digitsCache = new Map<string, number>();

/**
 * The number of minor-unit digits a currency has.
 *
 * Intl does not reject an unknown-but-well-formed code — it quietly answers 2 —
 * so this is not a validation step and does not pretend to be one. It does not
 * need to be: the only currencies that reach here are ones the API already
 * accepted against its own `currency` table, and `money.test.ts` pins the digits
 * Intl reports for every seeded currency against the model's own numbers. A
 * malformed code still throws, which is a bug in a caller rather than a
 * currency question.
 */
export function minorUnitDigits(currency: string): number {
  const cached = digitsCache.get(currency);
  if (cached !== undefined) return cached;

  // A fixed locale, and not the reader's: how many minor units a currency has is
  // a fact about the currency, not about who is looking. A cache keyed by
  // currency alone would otherwise be wrong the moment the two disagreed.
  //
  // Throws RangeError on a malformed code, which is the right outcome: it means
  // something other than a currency reached this function.
  const digits =
    new Intl.NumberFormat("de-DE", { style: "currency", currency }).resolvedOptions()
      .maximumFractionDigits ?? 2;

  digitsCache.set(currency, digits);
  return digits;
}

/** 10 ** digits, as an integer. */
function scaleFactor(currency: string): number {
  return 10 ** minorUnitDigits(currency);
}

/**
 * Formats minor units with the currency symbol: `-12,50 €`.
 *
 * Division by the scale factor is the one place a float appears, and it is safe
 * here: the result is immediately handed to Intl for display and never read back
 * as a value. Everything that *is* read back — parsing, and the amounts sent to
 * the API — stays integral.
 */
export function formatAmount(minor: number, currency: string, locale: Locale): string {
  return new Intl.NumberFormat(locale, { style: "currency", currency }).format(
    zeroed(minor) / scaleFactor(currency),
  );
}

/**
 * Turns negative zero into zero.
 *
 * IEEE-754 keeps the sign of -0 and Intl renders it faithfully as "-0,00 €",
 * which is a thing no amount of money has ever been. It arrives whenever a
 * screen negates a figure to change its point of view — a spent total shown as
 * an outflow, a remaining balance flipped for a bar — and the zero case is
 * exactly the one nobody tries by hand.
 */
function zeroed(minor: number): number {
  return minor === 0 ? 0 : minor;
}

/** Formats minor units without a symbol, for a column that has its own heading. */
export function formatPlain(minor: number, currency: string, locale: Locale): string {
  const digits = minorUnitDigits(currency);
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(zeroed(minor) / scaleFactor(currency));
}

/** Separators for the locale, read from Intl rather than assumed. */
function separators(locale: Locale): { group: string; decimal: string } {
  const parts = new Intl.NumberFormat(locale).formatToParts(1234.5);
  return {
    group: parts.find((p) => p.type === "group")?.value ?? ".",
    decimal: parts.find((p) => p.type === "decimal")?.value ?? ",",
  };
}

/**
 * Thrown when typed text cannot be read as an amount.
 *
 * It carries a message *key* rather than a sentence (K-36). The alternative —
 * building the German here — would have put half the interface's language in a
 * module about integer arithmetic, and it would have been the half nobody
 * remembers to translate, because it is only ever seen by somebody who typed
 * something wrong.
 *
 * `message` stays populated with the key so an uncaught one is still legible in
 * a stack trace.
 */
export class AmountError extends Error {
  readonly key: MessageKey;
  readonly params: Params;

  constructor(key: MessageKey, params: Params = {}) {
    super(key);
    this.name = "AmountError";
    this.key = key;
    this.params = params;
  }
}

/**
 * Reads what people actually type into signed minor units.
 *
 * This mirrors `pkg/money.Parse` deliberately and exactly, including what it
 * refuses. Accepted: `12,34`, `1.234,56`, `-12,34`, `12,34 €`, a bare `1234`,
 * `(12,34)` for a negative, and spaces anywhere. Refused: more decimal places
 * than the currency has — silently rounding somebody's input is worse than
 * rejecting it — and anything else ambiguous.
 *
 * Note the consequence of following the locale strictly: in de-DE the dot is a
 * *group* separator, so `12.50` is twelve hundred fifty, not twelve fifty — and
 * in en-GB the same text is twelve fifty. Both readings are correct for the
 * person typing them, which is exactly why the locale is a parameter and not a
 * constant: the alternative is telling a British reader that their `12.50` was
 * twelve hundred and fifty pounds, quietly, in a field they have already left.
 */
export function parseAmount(text: string, currency: string, locale: Locale): number {
  const digits = minorUnitDigits(currency);
  const { group, decimal } = separators(locale);

  let s = text.trim();
  if (s === "") throw new AmountError("money.empty");

  // Strip the currency symbol and any spacing, wherever the user put them.
  s = s.replace(/[€$£¥]/g, "").replace(new RegExp(escape(currency), "gi"), "");
  // \u00a0 and \u202f are a non-breaking space and a *narrow* no-break space —
  // the one Intl puts between a number and its currency symbol in de-DE and
  // fr-FR. `\s` does not match either, so pasting a formatted amount back into
  // the field would fail on a character nobody can see. Written as escapes for
  // the same reason: in literal form this class reads as three spaces.
  s = s.replace(/[\s\u00a0\u202f']/g, "");

  let negative = false;
  if (s.startsWith("-")) {
    [negative, s] = [true, s.slice(1)];
  } else if (s.startsWith("+")) {
    s = s.slice(1);
  } else if (s.startsWith("(") && s.endsWith(")")) {
    // Accounting notation for a negative, common in exported statements.
    [negative, s] = [true, s.slice(1, -1)];
  }
  if (s === "") throw new AmountError("money.noDigits", { text });

  // Order matters: in de-DE the group separator is the dot, so removing it first
  // stops "1.234" being read as one point two three four.
  s = s.split(group).join("");

  const at = s.indexOf(decimal);
  const whole = at === -1 ? s : s.slice(0, at);
  const fraction = at === -1 ? "" : s.slice(at + decimal.length);
  const hasFraction = at !== -1;

  if (fraction.includes(decimal)) {
    throw new AmountError("money.manySeparators", { text });
  }
  if (hasFraction && fraction === "") {
    throw new AmountError("money.trailingSeparator", { text });
  }
  if (whole === "" && !hasFraction) {
    throw new AmountError("money.noDigits", { text });
  }
  if (hasFraction && fraction.length > digits) {
    throw new AmountError("money.tooManyDecimals", {
      text,
      given: fraction.length,
      currency,
      digits,
    });
  }

  // Pad rather than truncate: "1,5" in EUR is 1.50, not 1.05.
  const padded = hasFraction ? fraction.padEnd(digits, "0") : "";
  const minor = onlyDigits(whole, text) * 10 ** digits + onlyDigits(padded, text);

  if (!Number.isSafeInteger(minor)) {
    throw new AmountError("money.tooLarge", { text });
  }
  return negative ? -minor : minor;
}

/** Converts a run of ASCII digits. Empty is zero, so ",50" works. */
function onlyDigits(s: string, original: string): number {
  if (s === "") return 0;
  if (!/^\d+$/.test(s)) throw new AmountError("money.notANumber", { text: original });
  return Number(s);
}

function escape(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * The currencies offered when recording what a foreign purchase actually cost
 * (K-18).
 *
 * They are the ones a household in the DACH region is most likely to have spent
 * in — a weekend in Zurich, London or New York — not the twenty-nine the ECB
 * quotes. The rate table carries all of those (`is_active = 0` in `currency`),
 * so widening this list later is a list change and nothing else.
 */
export const TRAVEL_CURRENCIES = ["CHF", "GBP", "USD", "DKK", "SEK", "NOK", "PLN", "CZK"] as const;
